/* eslint-disable @typescript-eslint/no-explicit-any */
import { store } from 'app/app-store';
import { AxiosResponse } from 'axios';
import { FLOWS } from 'common/constants';
import { pciPaymentsApiSender, pciPaymentsAuthSender } from 'utils/pci-payments-api-sender';
import { PaymentBody } from './payment';

const PAYMENT_SOURCE = 'registration_system' as const;

const PAYMENT_CATEGORY_BY_FLOW = {
  [FLOWS.APPLY_LICENSE]: 'application_fee',
  [FLOWS.RENEW_LICENSE]: 'renewal_fee',
  [FLOWS.BULK_RENEWAL]: 'renewal_fee',
  [FLOWS.PAY_TOT]: 'tot',
};

// Unauthenticated sender for the public/postback endpoints (stripe public key fetch,
// CardConnect/CyberSource postback pages), which are reached pre-auth and must stay that way.
// Its place-prefixing (getCityPath/setCurrentPlace) reads/writes the same static `place` field
// shared by every StrRegistrationAPISender/AuthSender instance, so v1 and v2 requests always
// agree on which city they're for without duplicating that logic.
const publicPost = (path: string, body: unknown) => pciPaymentsApiSender.post(path, body) as Promise<AxiosResponse>;

const publicGet = (path: string, params?: Record<string, unknown>) => pciPaymentsApiSender.get(path, params) as Promise<AxiosResponse>;

// Authenticated sender for payment-initiation calls: attaches `Authorization: Bearer <idToken>`
// (Cognito or admin-flow custom JWT, see utils/auth-sender.js) before every request, same
// instance base-strategy.js already uses for calculateMultiPropertyTot in the customer-portal
// TOT flow.
//
// AuthSender.post/get (plain JS) infer a `| undefined` return because of their bare early
// `return;` when ensureAuthenticationTokenIsAvailable() fails. These payment calls are only
// reached in already-authenticated flows, so assert back to AxiosResponse to match v1's
// (payment.ts) signatures -- PaymentApi in paymentResolver.ts requires the two modules line up.
const post = (path: string, body: unknown) => pciPaymentsAuthSender.post(path, body) as Promise<AxiosResponse>;

const get = (path: string, params?: Record<string, unknown>) => pciPaymentsAuthSender.get(path, params) as Promise<AxiosResponse>;

// v2 requires payment_id (number), subtotal, signature and credits -- none of which the
// v1-shaped PaymentBody callers pass in -- so they're pulled from the same paymentFees the
// fee calculation strategies already stored in redux, keeping caller signatures unchanged.
// credits is covered by the signature for every payment method, so it is taken from
// paymentFees rather than from the caller to guarantee it matches what was signed.
const toV2Body = ({ paymentId, ...body }: PaymentBody) => {
  const state = store.getState();
  const paymentFees = state?.network?.paymentFees as any | undefined;
  const licenseFromPayload = (state as any)?.payload?.license?.license;
  const permitNumber = (state?.network?.miscInfo as any)?.permitNumber || licenseFromPayload || state?.network?.applicationNumber;

  return {
    ...body,
    payment_id: Number(paymentId),
    subtotal: paymentFees?.summary?.total ?? 0,
    signature: paymentFees?.signature ?? '',
    credits: paymentFees?.summary?.credit ?? 0,
    payment_category: PAYMENT_CATEGORY_BY_FLOW[state?.app?.activeFlow],
    source: PAYMENT_SOURCE,
    permit_numbers: permitNumber ? [permitNumber] : [],
  };
};

export const payCC = (body: PaymentBody) => post('/payCC', toV2Body(body));

export const payACHDirectDebit = (body: PaymentBody) => post('/payACHDirectDebit', toV2Body(body));

export const payACH = payACHDirectDebit; // Renamed from payACH to payACHDirectDebit, but keeping the old name for backwards compatibility.

// Renamed from payCheck.
export const payCheck = (body: PaymentBody) => post('/payCheque', toV2Body(body));

export const payGeneric = (body: PaymentBody) => post('/payGeneric', toV2Body(body));

export const payACHCredit = (body: PaymentBody) => post('/payACHCredit', toV2Body(body));

// Renamed from stripe-public. Public/unauthenticated: fetches the publishable Stripe key.
export const getStripePublicKey = () => publicGet('/stripePublic', { source: PAYMENT_SOURCE });

// Public/unauthenticated: reached via CardConnect's hosted-tokenizer postback redirect, pre-auth.
export const cardConnectPostback = (place: string, body: unknown) => {
  pciPaymentsApiSender.setCurrentPlace(place);
  return publicPost('/cardConnectPostback', body);
};

// Public/unauthenticated: reached via CyberSource's Flex Microform postback redirect, pre-auth.
export const cyberSourceInitiatePayment = (place: string, body: unknown) => {
  pciPaymentsApiSender.setCurrentPlace(place);
  return publicPost('/cybersourcePostback', body);
};

// Both checkRenewalPayment and checkTotPayment unify to a single /paymentStatus endpoint in v2,
// mirroring portal-registration-generic-client's checkLicensePayment/checkTaxPayment pattern.
const paymentStatus = (paymentId: string | number) => get(`/paymentStatus/${paymentId}`);

export const checkRenewalPayment = paymentStatus;
export const checkTotPayment = paymentStatus;
